import { DEFAULT_IMAGE_DURATION,
  CHROMA_DEFAULT_KEY_COLOR, CHROMA_DEFAULT_OPACITY,
  CHROMA_DEFAULT_SIMILARITY,
  CHROMA_DEFAULT_SMOOTHNESS,
  CHROMA_DEFAULT_SPILL_REDUCTION,
  DEFAULT_IMAGE_HEIGHT,
  DEFAULT_IMAGE_WIDTH } from '~/constant'
import { TEXT_POSITION } from '~/enums'
import { assignPatch } from '~/hooks'
import TimelineCommonAsset from './TimelineCommonAsset'

/**
 * @typedef {object} ImageAssetSettings
 * @property {boolean} isChromaKeyEnabled
 * @property {RGBColor} chromaKeyColor
 * @property {number} similarity
 * @property {number} smoothness
 * @property {number} spillReduction
 * @property {number} chromaKeyOpacity
 */

/**
 * @memberOf Assets
 * @extends {TimelineCommonAsset}
 */
class ImageAsset extends TimelineCommonAsset {

  constructor(data = {}) {
    const { width = DEFAULT_IMAGE_WIDTH, height = DEFAULT_IMAGE_HEIGHT,
      settings = {}, userName, userWebPage } = data

    const {
      size = {
        width,
        height,
      },
      flip = {
        horizontal: false,
        vertical: false,
      },
      rotation = 0,
      opacity = 100,
      position = TEXT_POSITION.MIDDLE_CENTER,
      offset = { top: null, bottom: null, left: null, right: null },
      outlineColor = { r: 0, g: 0, b: 0, a: 1 },
      originalSize = { width, height },
      outlineWidth = 0,
      shadowColor = { r: 255, g: 255, b: 255, a: 1 },
      shadowBlur = 0,
      keepAspectRatio = true,
      aspectRatio = width / height,
      isChromaKeyEnabled = false,
      chromaKeyColor = CHROMA_DEFAULT_KEY_COLOR,
      similarity = CHROMA_DEFAULT_SIMILARITY,
      smoothness = CHROMA_DEFAULT_SMOOTHNESS,
      spillReduction = CHROMA_DEFAULT_SPILL_REDUCTION,
      chromaKeyOpacity = CHROMA_DEFAULT_OPACITY,
      relativeSize = { width: 100, height: 100 },
    } = settings

    const {
      url = null,
      duration = DEFAULT_IMAGE_DURATION,
      ...rest
    } = data

    super({ ...rest, duration })

    this._author = {
      name: userName,
      url: userWebPage,
    }

    this._url = url
    this._settings = {
      size,
      flip,
      rotation,
      opacity,
      position,
      offset,
      outlineColor,
      originalSize,
      outlineWidth,
      shadowColor,
      shadowBlur,
      keepAspectRatio,
      aspectRatio,
      isChromaKeyEnabled,
      chromaKeyColor,
      similarity,
      smoothness,
      spillReduction,
      chromaKeyOpacity,
      relativeSize,
    }

    this._maxDuration = Infinity
  }

  /**
   * @returns {string|null}
   */
  get url() {
    return this._url
  }

  /**
   * @returns {ImageAssetSettings}
   */
  get settings() {
    return this._settings
  }

  /**
   * Same behavior  as `setState` in React:
   * assigned value is merged over current one.
   *
   * @param {object} patch
   */
  set settings(patch) {
    this._settings = assignPatch(this._settings, patch)
  }

  /**
   * @returns {object}
   */
  get author() {
    return this._author
  }

  /**
  * @returns {string|null}
  */
  get fileId() {
    return super.fileId
  }

  /**
   * ImageAsset allows to assign related file later
   * (after all configured settings are uploaded and saved)
   *
   * @param {string} id
   */
  set fileId(id) {
    this._fileId = id
  }

  // eslint-disable-next-line class-methods-use-this
  get canHaveTransition() {
    return true
  }

  get mediaFileDuration() {
    return this._mediaFileDuration
  }

  set mediaFileDuration(duration) {
    this._mediaFileDuration = duration
  }

}

export default ImageAsset
